import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

interface ExercisePRChartProps {
  data: Record<string, unknown>;
}

const LIFT_COLORS = [
  "hsl(0 70% 55%)",
  "hsl(35 85% 55%)",
  "hsl(200 65% 55%)",
  "hsl(280 55% 55%)",
  "hsl(150 55% 50%)",
  "hsl(190 70% 50%)",
];

export function ExercisePRChart({ data }: ExercisePRChartProps) {
  const { chartData, liftNames } = useMemo(() => {
    const lifts = data.lifts as Record<string, { history?: Array<{ date: string; value: number }> }> | undefined;
    if (!lifts) return { chartData: [], liftNames: [] };

    const names = Object.keys(lifts).slice(0, 6);
    const dateMap = new Map<string, Record<string, number>>();

    for (const [name, lift] of Object.entries(lifts)) {
      if (!names.includes(name)) continue;
      for (const entry of lift.history ?? []) {
        const existing = dateMap.get(entry.date) ?? {};
        existing[name] = entry.value;
        dateMap.set(entry.date, existing);
      }
    }

    const sorted = [...dateMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, values]) => ({ date, ...values }));

    return { chartData: sorted, liftNames: names };
  }, [data]);

  const trainingAge = data.training_age as string | undefined;

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">1RM Trends</h3>
        <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
          No lift PR data yet. Record 1RM benchmarks to track strength gains.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">1RM Trends</h3>
        {trainingAge && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            {trainingAge}
          </span>
        )}
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={40} />
            <Tooltip
              contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
              formatter={(value: number) => [`${value} kg`, ""]}
            />
            <Legend wrapperStyle={{ fontSize: "10px" }} />
            {liftNames.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={LIFT_COLORS[i % LIFT_COLORS.length]}
                strokeWidth={1.5}
                dot={{ r: 2 }}
                connectNulls
                name={name}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
